import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const otpInputVariants = cva(
  // Base styles: Solid Square sizes based on breakpoint width to prevent <input> collapse. Default 44px for tiny phones, 48px for normal+ phones.
  "w-11 h-11 min-[380px]:w-12 min-[380px]:h-12 shrink-0 flex items-center justify-center text-center text-lg font-semibold font-['Be_Vietnam_Pro'] rounded-[10px] border duration-200 transition-colors focus:outline-none focus:ring-0",
  {
    variants: {
      variant: {
        default: "border-Color-Stroke-neutral bg-Color-Background-neutral-default_primary text-Color-Text-neutral-black hover:border-Color-Stroke-focus",
        filled: "border-Color-Stroke-neutral bg-Color-Background-neutral-filled text-Color-Text-neutral-black",
        focus: "border-Color-Stroke-focus bg-Color-Background-neutral-default_primary text-Color-Text-neutral-black",
        error: "border-Color-Stroke-negative bg-Color-Background-neutral-default_primary text-Color-Text-neutral-black",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface OtpInputProps {
  /** Current OTP value (string of digits) */
  value: string;
  /** Callback when OTP value changes */
  onChange: (value: string) => void;
  /** Callback when OTP is complete (all 6 digits entered) */
  onComplete?: (value: string) => void;
  /** Number of OTP digits (default: 6) */
  length?: number;
  /** Error state - shows red border */
  error?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Placeholder character for empty boxes */
  placeholder?: string;
  /** Disable interactions */
  disabled?: boolean;
}

const OtpInput = React.forwardRef<HTMLDivElement, OtpInputProps>(
  (
    {
      value = "",
      onChange,
      onComplete,
      length = 6,
      error = false,
      className,
      placeholder = "",
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const inputKeys = React.useMemo(
      () => Array.from({ length }, () => crypto.randomUUID()),
      [length]
    );
    // Ensure value doesn't exceed length
    const normalizedValue = value.slice(0, length);

    // Create array of individual digit values
    const digits = Array.from({ length }, (_, i) => normalizedValue[i] || "");

    // Track real focused index for instantaneous UI reaction
    const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

    const focusInput = (index: number) => {
      const input = inputRefs.current[index];
      if (input) {
        input.focus();
      }
    };

    const handleInputChange = (index: number, inputValue: string) => {
      // Only allow digits
      const digit = inputValue.replaceAll(/\D/g, "").slice(0, 1);

      // Create new value array
      const newDigits = [...digits];
      newDigits[index] = digit;

      // Join and update
      const newValue = newDigits.join("");
      onChange(newValue);

      // Auto-focus next input if digit was entered
      if (digit && index < length - 1) {
        focusInput(index + 1);
      }

      // Call onComplete if all digits are filled
      if (onComplete && newValue.length === length) {
        onComplete(newValue);
      }
    };

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e.key === "Backspace") {
        if (!digits[index] && index > 0) {
          // If current input is empty, focus previous and clear it
          focusInput(index - 1);
          const newDigits = [...digits];
          newDigits[index - 1] = "";
          onChange(newDigits.join(""));
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        focusInput(index - 1);
      } else if (e.key === "ArrowRight" && index < length - 1) {
        focusInput(index + 1);
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text")
        .replaceAll(/\D/g, "")
        .slice(0, length);
      onChange(pastedData);

      // Focus next empty input or last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      focusInput(nextIndex); // Call onComplete if pasted data fills all digits
      if (onComplete && pastedData.length === length) {
        onComplete(pastedData);
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "w-full flex justify-between items-center", // Justify between automatically handles the responsive spacing gracefully
          className,
        )}
        {...props}
      >
        {digits.map((digit, index) => {
          const isFocused = focusedIndex === index;
          const isFilled = !!digit;

          // Determine variant based on state
          let variant: "default" | "focus" | "filled" | "error" = "default";
          if (error) {
            variant = "error";
          } else if (isFocused) {
            variant = "focus";
          } else if (isFilled) {
            variant = "filled";
          }

          return (
            <input
              // use stable keys
              key={inputKeys[index]}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              placeholder={placeholder}
              className={cn(
                otpInputVariants({ variant }),
                disabled && "opacity-50 cursor-not-allowed",
              )}
              aria-label={`OTP digit ${index + 1} of ${length}`}
              aria-describedby={error ? "otp-error" : undefined}
              disabled={disabled}
            />
          );
        })}
      </div>
    );
  },
);

OtpInput.displayName = "OtpInput";

export { OtpInput };
